import { ID } from 'shared/types/id';
import { Timestamp } from 'shared/types/timestamp';

import { Category, ReorderPositions, SubCategory } from './types';

export type GoalReorderUpdate = {
  startDate?: Timestamp;
  deadline?: Timestamp;
  goalSorting?: ID[];
  lifeAreaId?: ID | null;
  parentIds?: ID[] | null;
};

export type GoalReorderChanges = {
  goals: { id: ID; update: GoalReorderUpdate }[];
  lifeAreas: { id: ID; update: { goalSorting: ID[] } }[];
};

export type ApplyGoalReorderOptions = {
  draggedId: string;
  targetId: string;
  position: ReorderPositions;
};

export type ApplyGoalReorderResult = {
  categories: Category[];
  changes: GoalReorderChanges;
};

const findGoalNode = (
  subCategories: SubCategory[],
  goalId: string,
): SubCategory | null =>
  subCategories.reduce<SubCategory | null>((found, subCategory) => {
    if (found) {
      return found;
    }
    if (subCategory.goal.id === goalId) {
      return subCategory;
    }
    return subCategory.subCategories.length
      ? findGoalNode(subCategory.subCategories, goalId)
      : null;
  }, null);

const detachGoal = (
  subCategories: SubCategory[],
  goalId: string,
): SubCategory | null => {
  let detached: SubCategory | null = null;

  subCategories.some((subCategory, index) => {
    if (subCategory.goal.id === goalId) {
      [detached] = subCategories.splice(index, 1);
      return true;
    }
    if (!subCategory.subCategories.length) {
      return false;
    }
    detached = detachGoal(subCategory.subCategories, goalId);
    return !!detached;
  });

  return detached;
};

const insertGoal = (
  subCategories: SubCategory[],
  targetId: string,
  position: ReorderPositions,
  dragged: SubCategory,
): boolean =>
  subCategories.some((subCategory, index) => {
    if (subCategory.goal.id === targetId) {
      if (position === ReorderPositions.Inside) {
        subCategory.subCategories.unshift(dragged);
        return true;
      }
      const insertIndex =
        position === ReorderPositions.Before ? index : index + 1;
      subCategories.splice(insertIndex, 0, dragged);
      return true;
    }
    return (
      !!subCategory.subCategories.length &&
      insertGoal(subCategory.subCategories, targetId, position, dragged)
    );
  });

// Moving before/after a goal reparents under that goal's parent; moving inside reparents under the goal itself.
const resolveParentGoal = (
  category: Category,
  targetGoal: SubCategory,
  position: ReorderPositions,
): SubCategory | null => {
  if (position === ReorderPositions.Inside) {
    return targetGoal;
  }
  if (
    position !== ReorderPositions.Before &&
    position !== ReorderPositions.After
  ) {
    return null;
  }
  const parentIds = targetGoal.goal.parentIds;
  return parentIds?.length
    ? findGoalNode(category.subCategories, parentIds[parentIds.length - 1])
    : null;
};

export const applyGoalReorder = (
  categories: Category[],
  { draggedId, targetId, position }: ApplyGoalReorderOptions,
): ApplyGoalReorderResult | null => {
  if (!targetId || draggedId === targetId) {
    return null;
  }

  const nextCategories = structuredClone(categories);
  const changes: GoalReorderChanges = { goals: [], lifeAreas: [] };

  const dragged = nextCategories.reduce<SubCategory | null>(
    (found, category) => found ?? detachGoal(category.subCategories, draggedId),
    null,
  );

  if (!dragged) {
    return null;
  }

  if (position === ReorderPositions.InsideCategory) {
    const targetCategory = nextCategories.find(({ id }) => id === targetId);
    if (!targetCategory) {
      return null;
    }

    targetCategory.subCategories.unshift(dragged);
    changes.goals.push({
      id: dragged.goal.id as ID,
      update: { lifeAreaId: targetCategory.id, parentIds: null },
    });
    changes.lifeAreas.push({
      id: targetCategory.id as ID,
      update: {
        goalSorting: targetCategory.subCategories.map(({ goal }) => goal.id),
      },
    });

    return { categories: nextCategories, changes };
  }

  let inserted = false;

  nextCategories.forEach((category) => {
    if (!insertGoal(category.subCategories, targetId, position, dragged)) {
      return;
    }
    inserted = true;

    const targetGoal = findGoalNode(category.subCategories, targetId);
    if (!targetGoal) {
      return;
    }

    const parentGoal = resolveParentGoal(category, targetGoal, position);

    changes.goals.push({
      id: dragged.goal.id as ID,
      update: {
        lifeAreaId: parentGoal ? null : (category.id as ID),
        parentIds: parentGoal
          ? [...(parentGoal.goal.parentIds ?? []), parentGoal.goal.id]
          : null,
      },
    });

    if (parentGoal) {
      changes.goals.push({
        id: parentGoal.goal.id as ID,
        update: {
          goalSorting: parentGoal.subCategories.map(({ goal }) => goal.id),
        },
      });
    }

    if (targetGoal.goal.lifeAreaId) {
      changes.lifeAreas.push({
        id: category.id as ID,
        update: {
          goalSorting: category.subCategories.map(({ goal }) => goal.id),
        },
      });
    }
  });

  return inserted ? { categories: nextCategories, changes } : null;
};
